import styled from '@emotion/styled';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useQuery } from '@tanstack/react-query';
import { Constants } from '@trinity/chat-components/components/Theme';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type Survey = {
  id: string;
  name: string;
};

type Props = {
  segmentId?: string;
  segmentName?: string;
  onSurveyClick?: (survey: Survey) => void;
};

export const SurveySidePanel = ({ segmentId, onSurveyClick }: Props) => {
  const apiClient = useChatApiClient();

  const { data: surveys = [], isLoading } = useQuery({
    queryKey: ['message-surveys', { segmentId }],
    queryFn: async () => {
      if (!segmentId) return [];
      return apiClient.surveys.getSurveys(segmentId);
    },
    enabled: !!segmentId,
  });

  return (
    <Root>
      <Content>
        <Header>
          <StyledAutoAwesomeIcon />
          <Title>Segment Surveys</Title>
          <CountBadge>{surveys.length}</CountBadge>
        </Header>
        <ScrollArea>
          {isLoading ? (
            <EmptyState>Loading surveys…</EmptyState>
          ) : surveys.length === 0 ? (
            <EmptyState>No surveys available for this segment</EmptyState>
          ) : (
            surveys.map((survey) => (
              <SurveyCard
                key={survey.id}
                onClick={() => onSurveyClick?.(survey)}
              >
                <SurveyName>{survey.name}</SurveyName>
              </SurveyCard>
            ))
          )}
        </ScrollArea>
      </Content>
    </Root>
  );
};

const Root = styled.div`
  box-sizing: border-box;
  border-radius: ${Constants.Modal.BorderRadius}px;
  height: 100%;
  width: 100%;
  background: ${({ theme }) => theme.palette.gray.light2};
  position: relative;
`;

const Content = styled.div`
  box-sizing: border-box;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  width: 100%;
  background: var(--pri-01);
`;

const StyledAutoAwesomeIcon = styled(AutoAwesomeIcon)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.primary.main,
}));

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px 0;
  margin-bottom: 8px;
`;

const Title = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
`;

const CountBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  background: #f0f0f0;
  border-radius: 10px;
  padding: 2px 8px;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 8px 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
  font-size: 14px;
  text-align: center;
  padding: 20px;
`;

const SurveyCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.palette.sentiment.primary.main};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const SurveyName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`;
